import { useState } from 'react';

import { Button, Chips, Modal, showSnackbar, Table, TabPage, TabView, TextInput } from 'fluxo-ui';
import { PlusIcon, SendIcon, TrashIcon, UploadIcon } from 'fluxo-ui/icons';

import CSVUploader from './CSVUploader';

import type { Column, ComponentEvent } from 'fluxo-ui';

interface Invite {
    id: string;
    email: string;
    name?: string;
    status: 'pending' | 'sent' | 'opened' | 'accessed' | 'completed' | 'expired';
    sentAt?: string;
    acceptedAt?: string;
    reminderCount: number;
}

export interface InviteEntry {
    name: string;
    email: string;
}

interface InviteManagerProps {
    examId?: string;
    invites?: Invite[];
    onInvite?: (invites: InviteEntry[]) => Promise<void>;
    onResend?: (inviteId: string) => Promise<void>;
    onRemove?: (inviteId: string) => Promise<void>;
    disabled?: boolean;
}

const inviteLabel = (invite: InviteEntry) => `${invite.name} <${invite.email}>`;

const InviteManager: React.FC<InviteManagerProps> = ({ invites = [], onInvite, onResend, onRemove, disabled = false }) => {
    const [inviteList, setInviteList] = useState<InviteEntry[]>([]);
    const [singleEmail, setSingleEmail] = useState('');
    const [singleName, setSingleName] = useState('');
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [sending, setSending] = useState(false);

    const handleAddEmail = () => {
        const email = singleEmail.trim();
        if (!email) return;

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showSnackbar('Please enter a valid email address', 'Validation Error', { type: 'error' });
            return;
        }

        if (inviteList.some((i) => i.email === email)) {
            showSnackbar('Email already in the list', 'Duplicate', { type: 'warning' });
            return;
        }

        // Backend requires a name — fall back to the email's local part
        const name = singleName.trim() || email.split('@')[0];
        setInviteList([...inviteList, { name, email }]);
        setSingleEmail('');
        setSingleName('');
    };

    const handleSendInvites = async () => {
        if (inviteList.length === 0) {
            showSnackbar('Please add at least one email', 'Validation Error', { type: 'error' });
            return;
        }

        setSending(true);
        try {
            await onInvite?.(inviteList);
            setInviteList([]);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to send invitations', 'Error', { type: 'error' });
        } finally {
            setSending(false);
        }
    };

    const handleResend = async (inviteId: string) => {
        try {
            await onResend?.(inviteId);
            showSnackbar('Invitation resent successfully', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to resend invitation', 'Error', { type: 'error' });
        }
    };

    const handleRemove = async (inviteId: string) => {
        try {
            await onRemove?.(inviteId);
            showSnackbar('Invitation removed', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to remove invitation', 'Error', { type: 'error' });
        }
    };

    const handleBulkUpload = (uploaded: Array<{ email: string; name?: string }>) => {
        const newInvites = uploaded
            .filter((u) => !inviteList.some((i) => i.email === u.email))
            .map((u) => ({ email: u.email, name: u.name?.trim() || u.email.split('@')[0] }));
        setInviteList([...inviteList, ...newInvites]);
        setShowBulkModal(false);
        showSnackbar(`${newInvites.length} email(s) added from CSV`, 'Success', { type: 'success' });
    };

    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            pending: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
            sent: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
            opened: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
            accessed: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
            completed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
            expired: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
        };
        return (
            <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium capitalize ${styles[status] || styles.pending}`}>
                {status}
            </span>
        );
    };

    const columns: Column[] = [
        {
            title: 'Email',
            field: 'email',
            sortable: true,
            template: (row: Invite) => (
                <div>
                    <div className="font-medium text-slate-900 dark:text-white">{row.email}</div>
                    {row.name && <div className="text-xs text-slate-500 dark:text-slate-400">{row.name}</div>}
                </div>
            ),
        },
        {
            title: 'Status',
            field: 'status',
            sortable: true,
            template: (row: Invite) => getStatusBadge(row.status),
        },
        {
            title: 'Sent At',
            field: 'sentAt',
            sortable: true,
            hideBelow: 'md',
            template: (row: Invite) => (
                <span className="text-sm text-slate-600 dark:text-slate-400">{row.sentAt ? new Date(row.sentAt).toLocaleString() : '-'}</span>
            ),
        },
        {
            title: 'Reminders',
            field: 'reminderCount',
            hideBelow: 'lg',
            template: (row: Invite) => <span className="text-sm text-slate-600 dark:text-slate-400">{row.reminderCount}</span>,
        },
        {
            title: 'Actions',
            field: 'actions',
            template: (row: Invite) => (
                <div className="flex items-center gap-1">
                    {(row.status === 'sent' || row.status === 'pending') && (
                        <button
                            onClick={() => handleResend(row.id)}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                            title="Resend invitation"
                            disabled={disabled}
                        >
                            <SendIcon className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={() => handleRemove(row.id)}
                        className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        title="Remove invitation"
                        disabled={disabled}
                    >
                        <TrashIcon className="w-4 h-4" />
                    </button>
                </div>
            ),
        },
    ];

    const getInviteStats = () => {
        const total = invites.length;
        const pending = invites.filter((i) => i.status === 'pending').length;
        const sent = invites.filter((i) => i.status === 'sent').length;
        const accepted = invites.filter((i) => i.status === 'opened' || i.status === 'accessed' || i.status === 'completed').length;
        const expired = invites.filter((i) => i.status === 'expired').length;

        return { total, pending, sent, accepted, expired };
    };

    const stats = getInviteStats();

    return (
        <>
            <div className="space-y-6">
                {/* Header with Stats */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                    <h3 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white mb-4">Invite Participants</h3>

                    {invites.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                            <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-xl">
                                <div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</div>
                                <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-0.5">Total</div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-xl">
                                <div className="text-2xl font-bold text-slate-700 dark:text-slate-300">{stats.pending}</div>
                                <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-0.5">Pending</div>
                            </div>
                            <div className="text-center p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl">
                                <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">{stats.sent}</div>
                                <div className="text-xs font-medium text-teal-600 dark:text-teal-400 mt-0.5">Sent</div>
                            </div>
                            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.accepted}</div>
                                <div className="text-xs font-medium text-green-600 dark:text-green-400 mt-0.5">Accepted</div>
                            </div>
                            <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.expired}</div>
                                <div className="text-xs font-medium text-red-600 dark:text-red-400 mt-0.5">Expired</div>
                            </div>
                        </div>
                    )}

                    <TabView>
                        {/* Manual Entry Tab */}
                        <TabPage header="Manual Entry" leftIcon={PlusIcon}>
                            <div className="p-4 space-y-4">
                                {/* Add Invite Fields */}
                                <div className="flex flex-col md:flex-row gap-3">
                                    <div className="flex-1">
                                        <TextInput
                                            value={singleName}
                                            onChange={(e: ComponentEvent<string>) => setSingleName(e.value)}
                                            placeholder="Participant name (optional)"
                                            disabled={disabled}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <TextInput
                                            type="email"
                                            value={singleEmail}
                                            onChange={(e: ComponentEvent<string>) => setSingleEmail(e.value)}
                                            placeholder="Enter email address"
                                            onKeyPress={(e: any) => {
                                                if (e.key === 'Enter') {
                                                    handleAddEmail();
                                                }
                                            }}
                                            disabled={disabled}
                                        />
                                    </div>
                                    <Button
                                        label="Add"
                                        leftIcon={PlusIcon}
                                        variant="primary"
                                        onClick={handleAddEmail}
                                        disabled={disabled || !singleEmail.trim()}
                                    />
                                </div>

                                {/* Invite List */}
                                {inviteList.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                Invite List ({inviteList.length})
                                            </div>
                                            <Button
                                                label="Clear All"
                                                variant="default"
                                                size="sm"
                                                layout="plain"
                                                onClick={() => setInviteList([])}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <Chips
                                            value={inviteList.map(inviteLabel)}
                                            onChange={(e: ComponentEvent<string[]>) =>
                                                setInviteList(inviteList.filter((invite) => e.value.includes(inviteLabel(invite))))
                                            }
                                            disabled={disabled}
                                        />
                                    </div>
                                )}

                                {/* Send Button */}
                                <div className="flex justify-end">
                                    <Button
                                        label={`Send ${inviteList.length} Invitation${inviteList.length !== 1 ? 's' : ''}`}
                                        leftIcon={SendIcon}
                                        variant="primary"
                                        onClick={handleSendInvites}
                                        isLoading={sending}
                                        disabled={disabled || sending || inviteList.length === 0}
                                    />
                                </div>
                            </div>
                        </TabPage>

                        {/* Bulk Upload Tab */}
                        <TabPage header="Bulk Upload" leftIcon={UploadIcon}>
                            <div className="p-4 space-y-4">
                                <div className="text-center py-8">
                                    <UploadIcon className="w-12 h-12 text-slate-400 opacity-40 mx-auto mb-4" />
                                    <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Upload CSV File</h4>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                        Upload a CSV file containing email addresses to invite multiple participants at once
                                    </p>
                                    <Button
                                        label="Upload CSV"
                                        leftIcon={UploadIcon}
                                        variant="primary"
                                        onClick={() => setShowBulkModal(true)}
                                        disabled={disabled}
                                    />
                                </div>

                                <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                                    <h5 className="font-medium text-teal-900 dark:text-teal-100 mb-2">CSV Format</h5>
                                    <p className="text-sm text-teal-800 dark:text-teal-200 mb-2">
                                        Your CSV file should have the following format:
                                    </p>
                                    <div className="bg-white dark:bg-slate-900 p-3 rounded border border-teal-300 dark:border-teal-700 font-mono text-xs">
                                        email,name
                                        <br />
                                        user1@example.com,John Doe
                                        <br />
                                        user2@example.com,Jane Smith
                                        <br />
                                        user3@example.com,Bob Johnson
                                    </div>
                                </div>
                            </div>
                        </TabPage>
                    </TabView>
                </div>

                {/* Invitations Table */}
                {invites.length > 0 && (
                    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                        <h3 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white mb-4">Sent Invitations</h3>
                        <Table
                            columns={columns}
                            rows={invites}
                            totalRows={invites.length}
                            pagination
                            rowsPerPage={10}
                            noRowsMessage="No invitations sent yet"
                        />
                    </div>
                )}
            </div>

            {/* CSV Upload Modal */}
            <Modal isOpen={showBulkModal} onClose={() => setShowBulkModal(false)} title="Upload CSV File" size="lg">
                <CSVUploader onUpload={handleBulkUpload} onCancel={() => setShowBulkModal(false)} />
            </Modal>
        </>
    );
};

export default InviteManager;
